import { useEffect, useState } from "react";

/**
 * Three-up image slider. Displays three images in one row; advances one
 * "page" (three images) at a time with translate-X. Auto-rotates; dot
 * navigation jumps between pages.
 *
 * Props:
 *   images   – array of { src, alt } objects. At least 3 recommended.
 *   interval – ms between auto-advances (default 6000). Pass 0 to disable.
 *   testid   – data-testid suffix for the root element.
 */
export default function ThreeImageSlider({
  images = [],
  interval = 6000,
  testid = "three-image-slider",
}) {
  const pageSize = 3;
  // Pad so every page has exactly 3 slots (last page wraps from the start).
  const remainder = images.length % pageSize;
  const padded =
    remainder === 0
      ? images
      : [...images, ...images.slice(0, pageSize - remainder)];
  const pageCount = padded.length / pageSize;

  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!interval || pageCount <= 1) return undefined;
    const id = setInterval(
      () => setPage((p) => (p + 1) % pageCount),
      interval
    );
    return () => clearInterval(id);
  }, [interval, pageCount]);

  if (padded.length === 0) return null;

  return (
    <section
      data-testid={testid}
      className="py-32 md:py-48 border-t border-[#E2E2DF]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateX(-${page * 100}%)` }}
          >
            {Array.from({ length: pageCount }).map((_, p) => (
              <div
                key={p}
                className="w-full shrink-0 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
                data-testid={`${testid}-page-${p}`}
                aria-hidden={p !== page}
              >
                {padded.slice(p * pageSize, p * pageSize + pageSize).map((img, i) => (
                  <div
                    key={`${p}-${i}`}
                    className="aspect-[4/5] overflow-hidden bg-[#111]"
                  >
                    <img
                      src={img.src}
                      alt={img.alt || ""}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {pageCount > 1 && (
          <div className="mt-10 flex items-center justify-end gap-3">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`${testid}-dot-${i}`}
                className={`h-px transition-all duration-500 ${
                  i === page
                    ? "w-12 bg-[#111111]"
                    : "w-6 bg-[#111111]/30 hover:bg-[#111111]/60"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
